/**
 * MarkdownLiveField — the React ⇄ CodeMirror 6 bridge for the single-pane markdown editor.
 *
 * Owns one `EditorView` for its lifetime. The view is created on mount and destroyed on unmount;
 * it is never recreated in response to prop changes, because tearing down the view would drop the
 * cursor, the selection and the undo history mid-edit. Props are instead pushed into the live view:
 *
 *   • `value`    → dispatched as a document replacement, but ONLY when it actually differs from the
 *                  current document. Every keystroke calls `onChange`, the parent re-renders with
 *                  the same string, and this guard is what stops that from echoing a redundant
 *                  transaction back into the editor and fighting the cursor.
 *   • `onChange` → held in a ref, so a new closure each render does not invalidate the view.
 *   • `readOnly` → swapped through a `Compartment`, CodeMirror's mechanism for reconfiguring one
 *                  extension in place.
 *
 * The document is plain markdown at all times — see `markdownLiveExtensions.ts` for why the live
 * formatting involves no conversion.
 */

import { useEffect, useRef } from "react";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import { EditorView, keymap, drawSelection } from "@codemirror/view";
import { history, defaultKeymap, historyKeymap } from "@codemirror/commands";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { indentOnInput } from "@codemirror/language";

import { cn } from "@/lib/utils";
import { markdownLiveFormatting } from "./markdownLiveExtensions";

export interface MarkdownLiveFieldProps {
  /** Current markdown source. */
  value: string;
  /** Called with the full markdown source on every edit. */
  onChange: (value: string) => void;
  /** Locks editing (status-based locks live in the parent). */
  readOnly?: boolean;
  /** Accessible name for the editing surface. Read once, when the view is created. */
  ariaLabel: string;
  /** Extra classes for the wrapper element. */
  className?: string;
}

/**
 * The smallest single replacement that turns `from` into `to`: trims the common prefix and suffix.
 * Exported for unit testing.
 */
export function minimalChange(
  from: string,
  to: string,
): { from: number; to: number; insert: string } {
  const max = Math.min(from.length, to.length);
  let start = 0;
  while (start < max && from.charCodeAt(start) === to.charCodeAt(start))
    start++;
  let endFrom = from.length;
  let endTo = to.length;
  while (
    endFrom > start &&
    endTo > start &&
    from.charCodeAt(endFrom - 1) === to.charCodeAt(endTo - 1)
  ) {
    endFrom--;
    endTo--;
  }
  return { from: start, to: endFrom, insert: to.slice(start, endTo) };
}

/** `EditorState.readOnly` alone does not expose the state to assistive tech, so set ARIA too. */
function readOnlyExtension(readOnly: boolean): Extension {
  return [
    EditorState.readOnly.of(readOnly),
    EditorView.editable.of(!readOnly),
    EditorView.contentAttributes.of({
      "aria-readonly": readOnly ? "true" : "false",
    }),
  ];
}

export function MarkdownLiveField({
  value,
  onChange,
  readOnly = false,
  ariaLabel,
  className,
}: MarkdownLiveFieldProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  // One compartment per editor instance — sharing one across mounted editors would be legal but
  // makes the reconfigure effect below read as if it were global.
  const readOnlyCompartment = useRef(new Compartment()).current;

  // Kept in a ref so the effect below can stay mount-only.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // `markdownLanguage` is the GFM-flavoured base — needed for tables, strikethrough and task
  // lists, which the CommonMark default does not parse. No `codeLanguages` are loaded: fenced
  // blocks only need monospace styling, and language-data would pull in every grammar.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const view = new EditorView({
      state: EditorState.create({
        doc: value,
        extensions: [
          history(),
          drawSelection(),
          indentOnInput(),
          keymap.of([...defaultKeymap, ...historyKeymap]),
          EditorView.lineWrapping,
          markdown({ base: markdownLanguage }),
          markdownLiveFormatting,
          readOnlyCompartment.of(readOnlyExtension(readOnly)),
          EditorView.contentAttributes.of({ "aria-label": ariaLabel }),
          EditorView.updateListener.of((update) => {
            if (update.docChanged)
              onChangeRef.current(update.state.doc.toString());
          }),
        ],
      }),
      parent: host,
    });

    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Mount-only by design; prop changes are pushed into the live view by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push external document changes (e.g. the artifact query refetching) into the view.
  //
  // Only the CHANGED span is replaced (common prefix/suffix trimmed). A whole-document replacement
  // maps every selection to an edge of the insert, so a resync to newer server content jumped the
  // cursor to the start/end of the document; replacing the minimal span lets CodeMirror map the
  // cursor through untouched text, keeping it in place when the change is elsewhere.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === value) return;
    view.dispatch({ changes: minimalChange(current, value) });
  }, [value]);

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: readOnlyCompartment.reconfigure(readOnlyExtension(readOnly)),
    });
  }, [readOnly]);

  return (
    <div
      ref={hostRef}
      data-testid="markdown-live-field"
      data-readonly={readOnly ? "true" : "false"}
      className={cn("flex flex-col overflow-hidden", className)}
    />
  );
}

export default MarkdownLiveField;
